import React, {useEffect, useImperativeHandle, useRef} from 'react';
import {clsx} from 'clsx';

import {Visibility, VisibilityEvents} from 'common/components/base/stackable';
import {SvgIconReact, SvgIconType} from 'common/components/svg-icon';
import {TooltipReact} from 'common/components/tooltip';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import {MetricType} from 'common/query-builder/query-block/constants';
import {RowActions} from 'common/query-builder/query-entry-section/components/actions';
import {RowLabel} from 'common/query-builder/query-entry-section/components/row-label';
import {
  Action,
  DROP_POSITION,
  Entry,
  EntryPart,
  isMetricEntry,
  RowLabelType,
} from 'common/query-builder/query-entry-section/types';
import {ReportName} from 'common/report/constants';
import {useDragAndDropManager} from 'common/contexts/react/drag-and-drop-manager-context';
import {SectionItem} from 'common/components/items-menu/types';

import {ControlBarActionItem, ControlBarActionItems} from '../constants';
import {TrackingEvent} from '../tracking';
import {EntryRowProps} from './types';

type Props = EntryRowProps<Entry> & {
  onDeleteEntry?: () => void;
  extraEntryClass?: Record<string, boolean>;
  hideActions?: boolean;
  isPlaceholder: boolean;
  renderActions?: () => React.ReactNode;
  renderBody?: () => React.ReactNode;
  renderSelector: (props: {rowIcon: SvgIconType | null; rowActions: React.ReactNode}) => React.ReactNode;
  canAlwaysDelete?: boolean;
  overflowControls: ControlBarActionItem[];
  entryIcon?: SvgIconType;
  showLabels?: boolean;
};

/**
 * BaseEntryRow provides the basic elements for every entry type
 * and provides render props for the [selector] and [body rows]
 * and [suffix] from the following layout:
 *
 * ===============================================================
 * [drag] [label] | [entry-icon] [ selector ] [actions] [overflow]
 *                | [    body (filters, etc)                     ]
 *                | [    suffix (measurement, etc)               ]
 * ===============================================================
 */
export const BaseEntryRow = React.forwardRef<HTMLDivElement, Props>((props, forwardedRef) => {
  const dragAndDropManager = useDragAndDropManager();

  const ref = useRef<HTMLDivElement>(null);

  useImperativeHandle(forwardedRef, () => ref.current);

  const isMetricEntryRow = isMetricEntry(props.entry);
  const {entryKey} = props.entry;
  const dropAreas = props.getDropAreas();
  const dropAllowedAbove = dropAreas.includes(DROP_POSITION.ABOVE);
  const dropAllowedBelow = dropAreas.includes(DROP_POSITION.BELOW);
  const dropAllowedOn = dropAreas.includes(DROP_POSITION.ON);
  const draggable = props.isDraggingEnabled();
  const isSectionDraggable = props.sectionProps.rowDraggable && props.sectionProps.rowLimit !== 1;
  const isDraggingAnything = !!dragAndDropManager.draggingEl;
  const isDraggableAnywhere = draggable && props.hasNewUI;
  const entries = props.getEntries(true);
  let isDropzoneEnabled = isDraggingAnything && dropAreas.length > 0;
  if (props.sectionProps.reportName === ReportName.Funnels && entries.length === 1) {
    isDropzoneEnabled = isDropzoneEnabled && !isMetricEntryRow;
  }
  let isNotDropArea = isDraggingAnything && dropAreas.length === 0;
  if (props.sectionProps.reportName === ReportName.Funnels && entries.length === 1) {
    isNotDropArea = isNotDropArea && !isMetricEntryRow;
  }
  const shouldRenderRowActions = !props.hasNewUI && !!props.sectionProps.listProperty && props.index === 0;
  const hasActions = !props.sectionProps.readonly && (!props.hideActions || shouldRenderRowActions);
  const showLabels = props.showLabels ?? props.sectionProps.rowLabelType !== RowLabelType.None;

  const entryIndexLabelToggle =
    `behavior` in props.entry ? props.entry.behavior.entryIndexLabelToggle : props.entry.entryIndexLabelToggle;
  const rowIcon =
    entryIndexLabelToggle && props.sectionProps.rowIndexToggleIcon ? props.sectionProps.rowIndexToggleIcon : null;

  useEffect(() => {
    const onDropdownOpen = () => props.onDropdownStateChanged(true);
    const onDropdownClose = () => props.onDropdownStateChanged(false);
    const onContextMenuClose = (ev: Event) => {
      props.onDropdownStateChanged(false);
      props.propagateEvent(ev);
    };

    ref.current?.addEventListener(VisibilityEvents[Visibility.Open], onDropdownOpen);
    ref.current?.addEventListener(VisibilityEvents[Visibility.Closed], onDropdownClose);
    ref.current?.addEventListener(`closeContextMenu`, onContextMenuClose);

    return () => {
      ref.current?.removeEventListener(VisibilityEvents[Visibility.Open], onDropdownOpen);
      ref.current?.removeEventListener(VisibilityEvents[Visibility.Closed], onDropdownClose);
      ref.current?.removeEventListener(`closeContextMenu`, onContextMenuClose);
    };
  }, []);

  const onLabelClick = () => {
    if (props.sectionProps.rowIndexToggleable && !isMetricEntry(props.entry)) {
      props.updateEntry(Action.Edit, {
        ...props.entry,
        entryIndexLabelToggle: !props.entry.entryIndexLabelToggle,
      });
      props.dispatchTrack(TrackingEvent.EventIndexLabelClicked, {
        ...props.getEntryTrackingProps(),
        'Previous Label': props.rowLabel,
      });
    }
  };

  const rowActions = shouldRenderRowActions ? (
    props.sectionProps.rowActions
  ) : (
    <RowActions
      entry={props.entry}
      hasNewUI={props.hasNewUI}
      hasBehavioralFrequencyFilter={props.hasBehavioralFrequencyFilter}
      overflowControls={props.overflowControls}
      overflowControlsContextPanelTransform={(item: SectionItem) => ControlBarActionItems[item?.value]?.tooltipData}
      isEmptyPlaceholder={props.isEmptyPlaceholder()}
      canAlwaysDeleteEntry={props.canAlwaysDelete || false}
      canDeleteEntry={props.canDelete}
      disabled={props.sectionProps.disabled}
      onClickDelete={props.onDeleteEntry ?? props.removeEntry}
      onControlSelect={props.onControlSelect}
      renderActions={props.renderActions}
    />
  );

  const draggingControl = isSectionDraggable && (
    <div
      className={clsx(`mp-query-entry-gripper`, {
        'is-placeholder': props.isPlaceholder,
        'is-dragging-disabled': !draggable,
      })}
      onMouseDown={props.onGripperMouseDown}
    >
      <SvgIconReact icon={props.hasNewUI ? `gripper-xs` : `gripper`} />
      {props.hasNewUI && !(props.isDraggingGripper || isDraggingAnything) && (
        <TooltipReact text='Drag to move' placement={Placement.Top} size={DisplaySize.Small} />
      )}
    </div>
  );

  return (
    <div
      ref={ref}
      className={clsx(
        `mp-query-entry`,
        {
          'drop-indicator-above': dropAllowedAbove && props.isDroppingAbove,
          'drop-indicator-below': dropAllowedBelow && props.isDroppingBelow,
          'drop-indicator-on': dropAllowedOn && props.isDroppingOn,
          'indent-drop-indicator': props.isNestedSection,
          'is-placeholder': props.isPlaceholder,
          'is-draggable': isSectionDraggable,
          'is-hidden': props.entry.isHidden,
          'is-draggable-anywhere': isDraggableAnywhere,
          'is-entry-selector-dropdown-open': props.isEntrySelectorDropdownOpen,
          'is-not-a-drop-area': isNotDropArea,
          'has-actions': hasActions,
          'has-labels': Boolean(showLabels || rowIcon),
          'has-filters': isMetricEntryRow
            ? false
            : (`filters` in props.entry && props.entry.filters?.length > 0) ||
              props.placeholder?.includes(EntryPart.Filters),
          'is-being-dragged': props.isDragging,
          [`is-${props.entry.type}`]: true,
          'is-exclusion-event': props.isExclusionStepsSection,
        },
        props.extraEntryClass,
      )}
      key={entryKey}
      elref='entryRow'
      draggable={draggable}
      disabled={props.sectionProps.disabled}
      onDragEnd={props.onEntryStepDragClear}
      onMouseDown={isDraggableAnywhere ? props.onGripperMouseDown : undefined}
      onDragStart={draggable ? props.onEntryStepDragStart : undefined}
      onDragEnter={draggable ? props.onEntryStepDragEnter : undefined}
      onDragOver={isDropzoneEnabled ? props.onEntryStepDragOver : undefined}
      onDragLeave={isDropzoneEnabled ? props.onEntryStepDragLeave : undefined}
      onDrop={isDropzoneEnabled ? props.onEntryStepDrop : undefined}
    >
      {/** The drop zones make drag and drop more forgiving by increasing the area with hidden elements */}
      {props.hasNewUI && isDropzoneEnabled && (
        <>
          <div
            className={clsx(`drop-zone-top`, `drop-zone`, {
              'is-first': props.index === 0 && !Number.isInteger(props.sectionProps.rowIndex),
            })}
          />
          <div
            className={clsx(`drop-zone-bottom`, `drop-zone`, {
              'is-last': props.index === entries.length - 1 && !Number.isInteger(props.sectionProps.rowIndex),
            })}
          />
        </>
      )}
      {/** prefix row */}
      {props.renderPrefix && props.renderPrefix({index: props.index, entry: props.entry})}

      {/** dragging control */}
      {!props.hasNewUI && draggingControl}

      {/** optional row label */}
      {showLabels && (
        <RowLabel
          index={props.index}
          entry={props.entry}
          isPlaceholder={props.isPlaceholder}
          reportName={
            isMetricEntry(props.entry)
              ? props.entry.behavior.type === MetricType.Funnel
                ? ReportName.Funnels
                : ReportName.Retention
              : props.sectionProps.reportName
          }
          hasNewUI={props.hasNewUI}
          isReferencedMetrics={props.sectionProps.isReferencedMetrics}
          rowIcon={rowIcon}
          rowLabel={props.rowLabel}
          rowLabelTheme={props.rowLabelTheme}
          // those props are special case for funnel event comparison & anyorder
          // this is the layout:
          // [rowLabelPrefix] [rowLabel] <event_name> [actions] [overflow]
          //  ^--- prefix is togglable to "any-order" icon when click if it's anyorder funnel
          onGroupClick={props.onGroupLabelClick}
          onClick={onLabelClick}
          rowIndexToggleable={props.sectionProps.rowIndexToggleable}
          rowLabelPrefix={props.sectionProps.rowLabelPrefix}
          rowLabelPrefixIcon={props.sectionProps.rowLabelPrefixIcon}
        />
      )}

      {/* entry icon */}
      {/* entry icon is used by filter, breakdown and metric-entry(funnel,retention) */}
      {/* it's different from <RowLabel />, which is used to display formula prefix  */}
      {props.hasNewUI && props.entryIcon && (
        <div className='mp-query-entry-icon'>
          <SvgIconReact icon={props.entryIcon} />
        </div>
      )}

      <div className='mp-query-entry-body'>
        {/* There are cases we may not have a selector since it exists elsewhere (e.g. a filter for single segment) */}
        {props.renderSelector && (
          <div
            className={clsx(`mp-query-entry-row`, `for-selector`, {
              'has-label': showLabels,
              'is-placeholder': props.isPlaceholder,
            })}
            elref='queryEntryRow'
          >
            {/** main selector body */}
            {/* rowActions is passed to selector renderer so that it can render the row's actions elsewhere. this is only used by old QB complex list filters */}
            {props.renderSelector({rowActions, rowIcon})}

            {/** row actions */}
            {hasActions && rowActions}
          </div>
        )}

        {/** body rows */}
        {props.renderBody && props.renderBody()}
      </div>

      {/** suffix row */}
      {props.renderSuffix && props.renderSuffix({index: props.index, entry: props.entry})}

      {/** dragging control */}
      {props.hasNewUI && draggingControl}
    </div>
  );
});
